import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";

import PropTypes from "prop-types";

import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import MainModalHeader from "../Headers/MainModalHeader";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import StepsCard from "../Cards/StepsCard";
import StepsNavButtons from "../Buttons/StepsNavButtons";

const BaseMultistepModal = forwardRef(
    (
        {
            isVisible,
            setIsVisible,
            title,
            titleIcon,
            steps,
            isSmall,
            isMedium,
            isLoading,
        },
        ref
    ) => {
        const [currentStep, setCurrentStep] = useState(1);
        const [currentStepAction, setCurrentStepAction] = useState({});

        const modalRef = useClickOutside(() => closeModal());

        useImperativeHandle(ref, () => ({
            nextPage() {
                nextStep();
            },

            previousPage() {
                previousStep();
            },
        }));

        useEffect(() => {
            if (currentStep) {
                setCurrentStepAction({
                    action: steps[currentStep - 1]?.action,
                    subAction: steps[currentStep - 1]?.subAction,
                });
            }
        }, [currentStep, steps]);

        const closeModal = () => {
            setIsVisible && setIsVisible(false);
            setCurrentStep(1);
        };

        const previousStep = () => {
            setCurrentStep((prevPage) =>
                prevPage === 1 ? prevPage : prevPage - 1
            );
        };

        const nextStep = () => {
            setCurrentStep((prevPage) => prevPage + 1);
        };

        const renderStep = () => {
            return (steps && steps[currentStep - 1].content()) || <></>;
        };

        const getStepsInfo = () =>
            steps.map((s, index) => ({
                number: index + 1,
                name: s.name,
            }));

        return (
            <>
                <div
                    className={`modal small-scroll-y ${
                        isVisible ? "show" : ""
                    }`}
                    id={`${title}-modal`}
                >
                    <div
                        ref={modalRef}
                        className="modal-content-wrapper lg-modal shadow"
                    >
                        <MainModalHeader
                            header={title}
                            icon={titleIcon}
                            closeModalHandler={closeModal}
                        />
                        <LoadingOverlay active={isLoading}>
                            <StepsCard
                                steps={getStepsInfo()}
                                activeNumber={currentStep}
                                disableShadow={true}
                                className={"pb-3"}
                            />
                            <div
                                className={`modal-content small-scroll-y overflow-auto ${
                                    isSmall ? "" : "h-with-scroll"
                                } ${
                                    isMedium ? "h-small-modal-with-scroll" : ""
                                }`}
                            >
                                {renderStep()}
                            </div>
                        </LoadingOverlay>

                        <div className="base modal-footer">
                            <StepsNavButtons
                                back={
                                    currentStep > 1
                                        ? { action: previousStep }
                                        : undefined
                                }
                                next={
                                    currentStepAction?.action && {
                                        action: currentStepAction.action
                                            .submitAction,
                                        label: currentStepAction.action.name,
                                        disabled:
                                            currentStepAction.action.disabled,
                                    }
                                }
                                subNext={
                                    currentStepAction?.subAction && {
                                        action: currentStepAction.subAction
                                            .submitAction,
                                        label: currentStepAction.subAction.name,
                                        disabled:
                                            currentStepAction.subAction
                                                .disabled,
                                    }
                                }
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }
);

BaseMultistepModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    setIsVisible: PropTypes.func.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    titleIcon: PropTypes.string,
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            content: PropTypes.func.isRequired,
            action: PropTypes.shape({
                name: PropTypes.string.isRequired,
                isSubmitting: PropTypes.bool,
                disabled: PropTypes.bool,
                submitAction: PropTypes.func.isRequired,
            }),
            subAction: PropTypes.shape({
                name: PropTypes.string.isRequired,
                isSubmitting: PropTypes.bool,
                disabled: PropTypes.bool,
                submitAction: PropTypes.func.isRequired,
            }),
        })
    ),
    isLoading: PropTypes.bool,
    isSmall: PropTypes.bool,
    isMedium: PropTypes.bool,
};

export default BaseMultistepModal;
